import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { isString } from "lodash";
import { useEffect, useState } from "react";
import { z } from "zod";
import type { ShellUser } from "../../../api/types";
import { safeRetrieveValue } from "../../../helpers/safeRetrieveValue";
import { fetchMonthlyUsage } from "../requests";
import { useBillCycleDay } from "./useBillCycleDay";
import { useMmToken } from "./useMMToken";

dayjs.extend(utc);
dayjs.extend(timezone);

export const monthlyUsageZod = z.object({
  summaries: z.array(z.any()),
  total_sent: z.number(),
  total_received: z.number(),
  total_billing_units: z.number(),
  total_opt_out: z.number(),
});
export type MonthlyUsage = z.infer<typeof monthlyUsageZod>;

export const useMonthlyUsage = (mmUser?: ShellUser) => {
  const accessToken = useMmToken();
  /** 
      TODO: Handle Anniversary billing vendor
      let isUseBillCycleDay = this.props.featureFlags.anniversaryBilling
      && isAnniversaryBillingVendor()
      && this.props.paymentType === POSTPAID */
  const isUseBillCycleDay = false;
  const billCycleDay = useBillCycleDay(isUseBillCycleDay);
  const [monthlyUsage, setMonthlyUsage] =
    useState<MonthlyUsage["total_billing_units"]>();

  const initMonthlyUsage = async (
    token: string,
    timezone: string,
    billCycleDay: number
  ) => {
    const { startDate, endDate } = getMonthBoundary(billCycleDay, timezone);
    const response = await fetchMonthlyUsage(token, {
      timezone: timezone,
      start_date: startDate.toString(),
      end_date: endDate.toString(),
    });
    const parsed = monthlyUsageZod.safeParse(response);
    if (parsed.success) {
      setMonthlyUsage(parsed.data.total_billing_units);
    } else {
      console.error("fetchMonthlyUsage_failedToParse: ", parsed.error);
    }
  };

  useEffect(() => {
    const timezone =
      mmUser?.settings &&
      safeRetrieveValue(mmUser.settings, "timezone", isString);

    if (timezone && accessToken && billCycleDay) {
      initMonthlyUsage(accessToken, timezone, billCycleDay);
    }
  }, [mmUser, accessToken, billCycleDay]);

  return monthlyUsage;
};

const getMonthBoundary = (cycleDay: number, timezone: string) => {
  const currentMoment = dayjs();
  const currentMonth = dayjs().tz(timezone).month();
  const startMonth =
    currentMoment.date() < cycleDay ? currentMonth - 1 : currentMonth;
  const endMonth = startMonth + 1;
  // setting date after month may cause the month to roll to the next month
  // if the cycleDay is greater than the maximum date of the month
  let startDate = currentMoment
    .clone()
    .startOf("day")
    .month(startMonth)
    .date(cycleDay);
  const startMonthBegin = currentMoment
    .clone()
    .startOf("month")
    .month(startMonth);
  if (startDate.isAfter(startMonthBegin, "month")) {
    // overflown to the next month
    startDate = startDate.startOf("month");
  }
  let endDate = currentMoment
    .clone()
    .month(endMonth)
    .date(cycleDay - 1)
    .endOf("day");
  const endMonthBegin = currentMoment.clone().startOf("month").month(endMonth);
  if (endDate.isAfter(endMonthBegin, "month")) {
    // overflown to the next month
    endDate = endDate.month(endMonth).endOf("month");
  } else if (endDate.isBefore(endMonthBegin, "month")) {
    // rolled back to the previous month if cycleDay - 1 is 0
    endDate = endDate.endOf("month");
  }
  return { startDate: startDate.format(), endDate: endDate.format() };
};
